import React from "react";

const PlugImageCard = ({
  plugs,
  plugsID,
  setError,
  setPlugs,
  setSuccess,
  setPlugsID,
  localPlugImages,
  setIsPlugChanges,
}) => {
  const total = localPlugImages?.length ?? 0;
  const selectedCount = plugs?.length ?? 0;

  const clearStatus = () => {
    setIsPlugChanges?.(1);
    setError?.(null);
    setSuccess?.(null);
  };

  const toggleOne = (item) => {
    const typeStr = String(item?.type);
    const id = item?.plug_image_id;

    if (plugs?.includes(typeStr)) {
      setPlugs(plugs.filter((t) => t !== typeStr));
      setPlugsID(plugsID.filter((pid) => pid !== id));
    } else {
      setPlugs([...(plugs || []), typeStr]);
      setPlugsID([...(plugsID || []), id]);
    }
    clearStatus();
  };

  const selectAll = () => {
    if (!localPlugImages?.length) return;
    setPlugs(localPlugImages.map((i) => String(i?.type)));
    setPlugsID(localPlugImages.map((i) => i?.plug_image_id));
    clearStatus();
  };

  const clearAll = () => {
    setPlugs([]);
    setPlugsID([]);
    clearStatus();
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Plug Images</h3>
          <p className="text-[11px] text-gray-600">
            Select all plug types that apply. Click a card to toggle selection.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-800">
            Selected: {selectedCount}/{total}
          </span>

          <div className="hidden sm:flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Mobile quick actions */}
      <div className="mb-3 flex gap-2 sm:hidden">
        <button
          type="button"
          onClick={selectAll}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          Clear
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 lg:grid-cols-4">
        {localPlugImages && localPlugImages.length > 0 ? (
          localPlugImages.map((m, key) => {
            const isSelected = plugs?.includes(String(m?.type));

            return (
              <article
                key={key}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => toggleOne(m)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleOne(m);
                  }
                }}
                className={`group relative w-full cursor-pointer rounded-2xl border bg-white p-3 shadow-sm transition
                  ${isSelected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200 hover:border-gray-300"}
                `}
              >
                {/* Selection badge */}
                <div
                  className={`absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white shadow-sm
                    ${isSelected ? "border-emerald-600 bg-emerald-600" : "border-gray-300 bg-white text-gray-400"}
                  `}
                  aria-hidden="true"
                >
                  {/* Checkmark SVG */}
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    className="h-3.5 w-3.5"
                  >
                    <path d="M5 10.5l3 3 7-7" />
                  </svg>
                </div>

                {/* Image */}
                <div className="mb-2 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                  <img
                    src={m?.photo_url}
                    alt={`Plug type ${m?.type}`}
                    className="h-28 w-full object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-900">
                    Type {m?.type}
                  </p>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold
                      ${isSelected ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-700"}
                    `}
                  >
                    {isSelected ? "Selected" : "Tap to select"}
                  </span>
                </div>
              </article>
            );
          })
        ) : (
          <div className="col-span-full rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-xs text-gray-600">
            No Plug Image Found
          </div>
        )}
      </div>
    </section>
  );
};

export default PlugImageCard;
